import { Vector3, MeshBuilder, StandardMaterial, Color3, Mesh } from '@babylonjs/core';
import { loadModel, playAnimation } from './ModelLoader';
/** 门派对应颜色 */
const FACTION_COLORS = {
    sword: { primary: new Color3(0.15, 0.45, 0.85), accent: new Color3(0.6, 0.8, 1.0), weapon: new Color3(0.85, 0.9, 1.0) },
    dao: { primary: new Color3(0.55, 0.2, 0.75), accent: new Color3(0.8, 0.5, 1.0), weapon: new Color3(0.5, 0.3, 0.2) },
    monk: { primary: new Color3(0.85, 0.6, 0.08), accent: new Color3(1.0, 0.85, 0.3), weapon: new Color3(0.6, 0.45, 0.1) },
    shadow: { primary: new Color3(0.2, 0.2, 0.25), accent: new Color3(0.4, 0.4, 0.45), weapon: new Color3(0.35, 0.35, 0.4) },
};
export class PlayerEntity {
    mesh;
    speed = 5.0;
    scene;
    faction;
    glbModel = null;
    currentAnim = 'idle';
    hp = 1000;
    maxHp = 1000;
    mp = 500;
    maxMp = 500;
    constructor(scene, data, faction) {
        this.scene = scene;
        this.faction = faction;
        // 角色根节点
        this.mesh = new Mesh('player', scene);
        // 构建默认几何体模型
        this.buildPrimitiveModel(scene, faction);
        // 初始位置
        this.mesh.position = new Vector3(data.x, 0, data.z);
        this.speed = data.speed || 5.0;
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        this.mp = data.mp;
        this.maxMp = data.maxMp;
        // 异步尝试加载 glb 模型
        this.tryLoadGLB(scene, faction);
    }
    buildPrimitiveModel(scene, faction) {
        const colors = FACTION_COLORS[faction];
        // ===== 腿 =====
        const leftLeg = MeshBuilder.CreateCylinder('lleg', { height: 0.7, diameterTop: 0.15, diameterBottom: 0.18, tessellation: 8 }, scene);
        leftLeg.position = new Vector3(-0.12, 0.35, 0);
        leftLeg.parent = this.mesh;
        const legMat = new StandardMaterial('legMat', scene);
        legMat.diffuseColor = new Color3(0.25, 0.22, 0.2);
        leftLeg.material = legMat;
        const rightLeg = leftLeg.clone('rleg');
        rightLeg.position.x = 0.12;
        rightLeg.parent = this.mesh;
        // ===== 靴子 =====
        const leftBoot = MeshBuilder.CreateBox('lboot', { width: 0.18, height: 0.08, depth: 0.25 }, scene);
        leftBoot.position = new Vector3(-0.12, 0.04, 0.03);
        leftBoot.parent = this.mesh;
        const bootMat = new StandardMaterial('bootMat', scene);
        bootMat.diffuseColor = new Color3(0.2, 0.15, 0.1);
        leftBoot.material = bootMat;
        const rightBoot = leftBoot.clone('rboot');
        rightBoot.position.x = 0.12;
        rightBoot.parent = this.mesh;
        // ===== 身体（袍服） =====
        const body = MeshBuilder.CreateCylinder('body', {
            height: 0.9, diameterTop: 0.4, diameterBottom: 0.55, tessellation: 10
        }, scene);
        body.position.y = 1.05;
        body.parent = this.mesh;
        const bodyMat = new StandardMaterial('bodyMat', scene);
        bodyMat.diffuseColor = colors.primary;
        bodyMat.specularColor = new Color3(0.2, 0.2, 0.2);
        body.material = bodyMat;
        // ===== 腰带 =====
        const belt = MeshBuilder.CreateTorus('belt', { diameter: 0.5, thickness: 0.06, tessellation: 16 }, scene);
        belt.position.y = 0.65;
        belt.parent = this.mesh;
        const beltMat = new StandardMaterial('beltMat', scene);
        beltMat.diffuseColor = colors.accent;
        beltMat.emissiveColor = colors.accent.scale(0.15);
        belt.material = beltMat;
        // ===== 肩甲 =====
        const leftShoulder = MeshBuilder.CreateSphere('lshoulder', { diameter: 0.22, segments: 6 }, scene);
        leftShoulder.position = new Vector3(-0.3, 1.45, 0);
        leftShoulder.parent = this.mesh;
        const shoulderMat = new StandardMaterial('shoulderMat', scene);
        shoulderMat.diffuseColor = colors.accent;
        shoulderMat.emissiveColor = colors.accent.scale(0.1);
        leftShoulder.material = shoulderMat;
        const rightShoulder = leftShoulder.clone('rshoulder');
        rightShoulder.position.x = 0.3;
        rightShoulder.parent = this.mesh;
        // ===== 手臂 =====
        const leftArm = MeshBuilder.CreateCylinder('larm', { height: 0.55, diameter: 0.1, tessellation: 6 }, scene);
        leftArm.position = new Vector3(-0.3, 1.15, 0);
        leftArm.parent = this.mesh;
        leftArm.material = bodyMat;
        const rightArm = leftArm.clone('rarm');
        rightArm.position.x = 0.3;
        rightArm.parent = this.mesh;
        // ===== 脖子 =====
        const neck = MeshBuilder.CreateCylinder('neck', { height: 0.12, diameter: 0.15, tessellation: 6 }, scene);
        neck.position.y = 1.55;
        neck.parent = this.mesh;
        const skinMat = new StandardMaterial('skinMat', scene);
        skinMat.diffuseColor = new Color3(0.9, 0.75, 0.6);
        neck.material = skinMat;
        // ===== 头部 =====
        const head = MeshBuilder.CreateSphere('head', { diameter: 0.4, segments: 10 }, scene);
        head.position.y = 1.8;
        head.parent = this.mesh;
        head.material = skinMat;
        // ===== 发型/头饰（区分门派） =====
        this.createHair(scene, faction, colors);
        // ===== 武器 =====
        this.createWeapon(scene, faction, colors);
    }
    createHair(scene, faction, colors) {
        const hairMat = new StandardMaterial('hairMat', scene);
        switch (faction) {
            case 'sword': {
                // 剑宗：长发束冠
                hairMat.diffuseColor = new Color3(0.15, 0.1, 0.08);
                const hair = MeshBuilder.CreateSphere('hair', { diameter: 0.42, segments: 8 }, scene);
                hair.position.y = 1.85;
                hair.position.z = -0.02;
                hair.scaling = new Vector3(1, 0.9, 1.1);
                hair.parent = this.mesh;
                hair.material = hairMat;
                // 发冠
                const crown = MeshBuilder.CreateTorus('crown', { diameter: 0.25, thickness: 0.03, tessellation: 12 }, scene);
                crown.position.y = 2.0;
                crown.parent = this.mesh;
                const crownMat = new StandardMaterial('crownMat', scene);
                crownMat.diffuseColor = new Color3(0.8, 0.7, 0.3);
                crownMat.emissiveColor = new Color3(0.2, 0.18, 0.05);
                crown.material = crownMat;
                break;
            }
            case 'dao': {
                // 道宗：道冠
                hairMat.diffuseColor = new Color3(0.2, 0.15, 0.1);
                const hair = MeshBuilder.CreateSphere('hair', { diameter: 0.42, segments: 8 }, scene);
                hair.position.y = 1.85;
                hair.parent = this.mesh;
                hair.material = hairMat;
                const daoCrown = MeshBuilder.CreateCylinder('daoCrown', { height: 0.12, diameter: 0.2, tessellation: 8 }, scene);
                daoCrown.position.y = 2.05;
                daoCrown.parent = this.mesh;
                const daoMat = new StandardMaterial('daoMat', scene);
                daoMat.diffuseColor = new Color3(0.6, 0.3, 0.8);
                daoMat.emissiveColor = new Color3(0.15, 0.08, 0.2);
                daoCrown.material = daoMat;
                break;
            }
            case 'monk': {
                // 少林：光头
                const head2 = MeshBuilder.CreateSphere('baldHead', { diameter: 0.41, segments: 10 }, scene);
                head2.position.y = 1.81;
                head2.parent = this.mesh;
                const baldMat = new StandardMaterial('baldMat', scene);
                baldMat.diffuseColor = new Color3(0.88, 0.75, 0.6);
                baldMat.specularColor = new Color3(0.3, 0.3, 0.3);
                head2.material = baldMat;
                // 佛珠
                const beads = MeshBuilder.CreateTorus('beads', { diameter: 0.35, thickness: 0.025, tessellation: 20 }, scene);
                beads.position.y = 1.5;
                beads.rotation.x = Math.PI / 6;
                beads.parent = this.mesh;
                const beadMat = new StandardMaterial('beadMat', scene);
                beadMat.diffuseColor = new Color3(0.6, 0.4, 0.1);
                beads.material = beadMat;
                break;
            }
            case 'shadow': {
                // 唐门：面纱
                hairMat.diffuseColor = new Color3(0.1, 0.1, 0.12);
                const hair = MeshBuilder.CreateSphere('hair', { diameter: 0.42, segments: 8 }, scene);
                hair.position.y = 1.85;
                hair.parent = this.mesh;
                hair.material = hairMat;
                // 面纱/面罩
                const veil = MeshBuilder.CreatePlane('veil', { width: 0.35, height: 0.15 }, scene);
                veil.position = new Vector3(0, 1.72, 0.18);
                veil.parent = this.mesh;
                const veilMat = new StandardMaterial('veilMat', scene);
                veilMat.diffuseColor = new Color3(0.15, 0.15, 0.18);
                veilMat.backFaceCulling = false;
                veil.material = veilMat;
                break;
            }
        }
    }
    createWeapon(scene, faction, colors) {
        const weaponMat = new StandardMaterial('weaponMat', scene);
        weaponMat.specularColor = new Color3(0.8, 0.8, 0.8);
        switch (faction) {
            case 'sword': {
                // 长剑
                weaponMat.diffuseColor = colors.weapon;
                weaponMat.emissiveColor = new Color3(0.1, 0.15, 0.25);
                const blade = MeshBuilder.CreateBox('blade', { width: 0.04, height: 1.0, depth: 0.12 }, scene);
                blade.position = new Vector3(0.35, 1.2, -0.1);
                blade.rotation.z = -0.25;
                blade.parent = this.mesh;
                blade.material = weaponMat;
                // 剑柄
                const hilt = MeshBuilder.CreateCylinder('hilt', { height: 0.2, diameter: 0.06 }, scene);
                hilt.position = new Vector3(0.35, 0.68, -0.1);
                hilt.parent = this.mesh;
                const hiltMat = new StandardMaterial('hiltMat', scene);
                hiltMat.diffuseColor = new Color3(0.5, 0.35, 0.15);
                hilt.material = hiltMat;
                // 剑格
                const guard = MeshBuilder.CreateBox('guard', { width: 0.2, height: 0.03, depth: 0.06 }, scene);
                guard.position = new Vector3(0.35, 0.72, -0.1);
                guard.parent = this.mesh;
                guard.material = hiltMat;
                break;
            }
            case 'dao': {
                // 拂尘/法杖
                weaponMat.diffuseColor = new Color3(0.55, 0.35, 0.2);
                const staff = MeshBuilder.CreateCylinder('staff', { height: 1.6, diameter: 0.05, tessellation: 6 }, scene);
                staff.position = new Vector3(0.4, 1.2, 0);
                staff.parent = this.mesh;
                staff.material = weaponMat;
                // 杖头宝珠
                const orb = MeshBuilder.CreateSphere('orb', { diameter: 0.15, segments: 8 }, scene);
                orb.position = new Vector3(0.4, 2.0, 0);
                orb.parent = this.mesh;
                const orbMat = new StandardMaterial('orbMat', scene);
                orbMat.diffuseColor = new Color3(0.6, 0.3, 0.9);
                orbMat.emissiveColor = new Color3(0.3, 0.15, 0.5);
                orb.material = orbMat;
                break;
            }
            case 'monk': {
                // 禅杖
                weaponMat.diffuseColor = new Color3(0.6, 0.45, 0.12);
                const pole = MeshBuilder.CreateCylinder('pole', { height: 1.9, diameter: 0.07, tessellation: 8 }, scene);
                pole.position = new Vector3(0.4, 1.0, 0);
                pole.parent = this.mesh;
                pole.material = weaponMat;
                // 月牙形禅杖头
                const headPiece = MeshBuilder.CreateTorus('headPiece', { diameter: 0.2, thickness: 0.03, tessellation: 12 }, scene);
                headPiece.position = new Vector3(0.4, 1.95, 0);
                headPiece.parent = this.mesh;
                headPiece.material = weaponMat;
                break;
            }
            case 'shadow': {
                // 短匕首 x2
                weaponMat.diffuseColor = colors.weapon;
                const dagger1 = MeshBuilder.CreateBox('dagger1', { width: 0.03, height: 0.4, depth: 0.06 }, scene);
                dagger1.position = new Vector3(0.32, 0.95, 0.1);
                dagger1.rotation.z = -0.4;
                dagger1.parent = this.mesh;
                dagger1.material = weaponMat;
                const dagger2 = MeshBuilder.CreateBox('dagger2', { width: 0.03, height: 0.4, depth: 0.06 }, scene);
                dagger2.position = new Vector3(-0.32, 0.95, 0.1);
                dagger2.rotation.z = 0.4;
                dagger2.parent = this.mesh;
                dagger2.material = weaponMat;
                break;
            }
        }
    }
    async tryLoadGLB(scene, faction) {
        const modelPath = `/models/${faction}_character.glb`;
        const model = await loadModel(scene, modelPath);
        if (model) {
            // 清除所有几何体子网格
            this.mesh.getChildMeshes().forEach(m => m.dispose());
            // 将 glb 根节点挂到 this.mesh 下
            model.root.parent = this.mesh;
            model.root.scaling.setAll(0.01); // Mixamo 模型通常需要缩放
            this.glbModel = model;
            console.log(`[Player] 已加载 ${faction} glb 模型`);
        }
    }
    move(dx, dz, dt) {
        const moveAmount = this.speed * dt;
        this.mesh.position.x += dx * moveAmount;
        this.mesh.position.z += dz * moveAmount;
        if (dx !== 0 || dz !== 0) {
            this.mesh.rotation.y = Math.atan2(dx, dz);
        }
        // 简单走路动画（腿部摆动）
        if (!this.glbModel) {
            const t = performance.now() * 0.008;
            const swing = Math.sin(t) * 0.3;
            const lleg = this.mesh.getChildMeshes().find(m => m.name === 'lleg');
            const rleg = this.mesh.getChildMeshes().find(m => m.name === 'rleg');
            if (lleg)
                lleg.rotation.x = swing;
            if (rleg)
                rleg.rotation.x = -swing;
            const larm = this.mesh.getChildMeshes().find(m => m.name === 'larm');
            const rarm = this.mesh.getChildMeshes().find(m => m.name === 'rarm');
            if (larm)
                larm.rotation.x = -swing * 0.7;
            if (rarm)
                rarm.rotation.x = swing * 0.7;
        }
        else if (this.currentAnim !== 'run') {
            playAnimation(this.glbModel.animations, 'run');
            this.currentAnim = 'run';
        }
        // 边界限制
        const limit = 95;
        this.mesh.position.x = Math.max(-limit, Math.min(limit, this.mesh.position.x));
        this.mesh.position.z = Math.max(-limit, Math.min(limit, this.mesh.position.z));
    }
    idle() {
        // 还原腿部/手臂角度
        if (!this.glbModel) {
            const children = this.mesh.getChildMeshes();
            for (const m of children) {
                if (['lleg', 'rleg', 'larm', 'rarm'].includes(m.name)) {
                    m.rotation.x *= 0.85; // 平滑回到0
                }
            }
            // 轻微呼吸动画
            const t = performance.now() * 0.002;
            const body = children.find(m => m.name === 'body');
            if (body)
                body.scaling.y = 1 + Math.sin(t) * 0.01;
        }
        else if (this.currentAnim !== 'idle') {
            playAnimation(this.glbModel.animations, 'idle');
            this.currentAnim = 'idle';
        }
    }
    updateFromServer(data) {
        this.hp = data.hp;
        this.maxHp = data.maxHp;
        this.mp = data.mp;
        this.maxMp = data.maxMp;
        this.speed = data.speed;
        if (data.isDead) {
            this.mesh.scaling = new Vector3(1, 0.2, 1);
            if (this.glbModel && this.currentAnim !== 'death') {
                playAnimation(this.glbModel.animations, 'death', false);
                this.currentAnim = 'death';
            }
        }
        else {
            this.mesh.scaling = Vector3.One();
        }
    }
}
